import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  Truck,
  User,
  X,
} from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useOwnersStore, type Owner } from '../../store/ownersStore';
import { useUsersStore } from '../../store/usersStore';
import { useStockRequestSettingsStore } from '../../store/stockRequestSettingsStore';
import { useStockUiStore } from '../../store/stockUiStore';
import { ManageSuppliersPanel } from './requests/ManageSuppliersPanel';

interface Props {
  onClose: () => void;
}

type PrefSection = 'owners' | 'suppliers' | 'requests';

// ── Accordion section (matches NetBuilder Settings) ───────────────────────────
const AccordionSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  isDark: boolean;
  children: React.ReactNode;
}> = ({ title, icon, expanded, onToggle, isDark, children }) => {
  const hoverBg = isDark ? 'hover:bg-[#2b3140]/50' : 'hover:bg-slate-800/50';

  return (
    <div className="overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-2 px-1 rounded transition-colors ${hoverBg}`}
      >
        <div className="flex items-center gap-2">
          <span className={isDark ? 'text-gray-300' : 'text-blue-400'}>{icon}</span>
          <span className="font-medium text-sm">{title}</span>
        </div>
        {expanded ? (
          <ChevronDown size={16} className="text-gray-400 transition-transform" />
        ) : (
          <ChevronRight size={16} className="text-gray-400 transition-transform" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pt-1.5 pb-2 pl-6 space-y-2">{children}</div>
      </div>
    </div>
  );
};

// ── Single owner row ──────────────────────────────────────────────────────────
const OwnerRow: React.FC<{
  owner: Owner;
  isDark: boolean;
  onRename: (id: string, name: string, notes: string) => void;
  onDelete: (id: string) => void;
}> = ({ owner, isDark, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(owner.name);
  const [editNotes, setEditNotes] = useState(owner.notes ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const border = isDark ? '#343a43' : '#475569';
  const text = isDark ? '#f3f4f6' : '#e2e8f0';
  const muted = isDark ? '#9ca3af' : '#94a3b8';
  const inputBg = isDark ? '#171a1f' : '#334155';
  const hoverBg = isDark ? '#262b35' : '#1e293b';

  const startEdit = () => {
    setEditName(owner.name);
    setEditNotes(owner.notes ?? '');
    setConfirmDelete(false);
    setEditing(true);
    setTimeout(() => nameRef.current?.select(), 0);
  };

  const commitEdit = () => {
    const name = editName.trim();
    if (name && (name !== owner.name || editNotes.trim() !== (owner.notes ?? ''))) {
      onRename(owner.id, name, editNotes);
    }
    setEditing(false);
  };

  return (
    <div
      className="group flex items-center gap-2.5 px-2 py-2 rounded transition-colors"
      onMouseEnter={e => {
        if (!editing) (e.currentTarget as HTMLElement).style.backgroundColor = hoverBg;
      }}
      onMouseLeave={e => {
        if (!editing) (e.currentTarget as HTMLElement).style.backgroundColor = '';
      }}
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: isDark ? '#374151' : '#475569' }}
      >
        <User size={12} style={{ color: muted }} />
      </div>

      {editing ? (
        <div className="flex-1 flex flex-col gap-1.5 py-0.5">
          <input
            ref={nameRef}
            type="text"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="text-sm px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ backgroundColor: inputBg, borderColor: border, color: text }}
            placeholder="Owner name…"
          />
          <input
            type="text"
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="text-xs px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ backgroundColor: inputBg, borderColor: border, color: muted }}
            placeholder="Notes (optional)…"
          />
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: text }}>{owner.name}</p>
          {owner.notes && (
            <p className="text-xs truncate mt-0.5" style={{ color: muted }}>{owner.notes}</p>
          )}
        </div>
      )}

      {editing ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={commitEdit}
            disabled={!editName.trim()}
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ backgroundColor: isDark ? '#1a3a1a' : '#14532d', color: '#4ade80' }}
            title="Save"
          >
            <Check size={13} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="p-1.5 rounded transition-colors"
            style={{ backgroundColor: isDark ? '#2b3140' : '#334155', color: muted }}
            title="Cancel"
          >
            <X size={13} />
          </button>
        </div>
      ) : confirmDelete ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => { onDelete(owner.id); setConfirmDelete(false); }}
            className="text-[10px] px-2 py-0.5 rounded bg-red-500/80 hover:bg-red-600 text-white transition-colors"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="text-[10px] px-2 py-0.5 rounded transition-colors"
            style={{ color: muted, backgroundColor: isDark ? '#2b3140' : '#334155' }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={startEdit}
            className="p-1.5 rounded transition-colors hover:opacity-80"
            style={{ color: muted }}
            title="Edit"
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="p-1.5 rounded transition-colors hover:text-red-400"
            style={{ color: muted }}
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main drawer ───────────────────────────────────────────────────────────────
const StockPreferencesDrawer: React.FC<Props> = ({ onClose }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const { owners, isLoading, error, fetchOwners, addOwner, updateOwner, removeOwner } = useOwnersStore();
  const { users, fetchUsers } = useUsersStore();
  const {
    settings,
    isLoading: loadingSettings,
    error: settingsError,
    fetchSettings,
    saveSettings,
  } = useStockRequestSettingsStore();
  const { requestsViewMode, setRequestsViewMode } = useStockUiStore();

  const [visible, setVisible] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<PrefSection, boolean>>({
    owners: true,
    suppliers: false,
    requests: false,
  });

  const [newName, setNewName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const newNameRef = useRef<HTMLInputElement>(null);

  const [ordererIds, setOrdererIds] = useState<string[]>([]);
  const [savingApprovers, setSavingApprovers] = useState(false);
  const [approversSaved, setApproversSaved] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => { fetchOwners(); }, [fetchOwners]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => {
    if (settings) {
      const managers = settings.defaultOrdererIds.length > 0
        ? settings.defaultOrdererIds
        : settings.defaultApproverIds;
      setOrdererIds(managers);
    }
  }, [settings]);
  useEffect(() => { if (adding) newNameRef.current?.focus(); }, [adding]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 260);
  };

  const toggleSection = (key: PrefSection) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveApprovers = async () => {
    setSavingApprovers(true);
    setApproversSaved(false);
    const ok = await saveSettings(ordererIds, ordererIds);
    setSavingApprovers(false);
    if (ok) {
      setApproversSaved(true);
      setTimeout(() => setApproversSaved(false), 2500);
    }
  };

  const toggleOrderer = (userId: string) => {
    setOrdererIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    );
    setApproversSaved(false);
  };

  const submitAdd = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    const before = useOwnersStore.getState().owners.length;
    await addOwner(name, newNotes.trim());
    const after = useOwnersStore.getState().owners.length;
    setSaving(false);
    if (after > before) {
      setNewName('');
      setNewNotes('');
      setAdding(false);
    }
  };

  const cancelAdd = () => {
    setNewName('');
    setNewNotes('');
    setAdding(false);
  };

  // Match Edit Warehouses drawer shell; content chrome matches NetBuilder Settings
  const bg = isDark ? '#171a1f' : '#0f172a';
  const border = isDark ? '#343a43' : '#334155';
  const text = isDark ? '#f3f4f6' : '#f1f5f9';
  const muted = isDark ? '#9ca3af' : '#94a3b8';
  const inputBg = isDark ? '#20242b' : '#1e293b';
  const surface = isDark ? '#20242b' : '#1e293b';

  const TRANSITION = 'transform 260ms cubic-bezier(0.4,0,0.2,1)';

  const supplierTheme = {
    text,
    muted,
    border,
    inputBg,
    cardBg: surface,
    rowHover: isDark ? '#262b35' : '#334155',
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{
          backgroundColor: 'rgba(0,0,0,0.35)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 260ms ease',
        }}
        onMouseDown={handleClose}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex flex-col w-96 shadow-2xl text-white ${
          isDark ? 'settings-dark-neutral' : ''
        }`}
        style={{
          backgroundColor: bg,
          borderLeft: `1px solid ${border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: TRANSITION,
        }}
      >
        {/* Header — matches NetBuilder Settings */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${border}` }}
        >
          <h2 className={`text-xl font-semibold ${isDark ? 'text-gray-200' : 'text-blue-400'}`}>
            Preferences
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-slate-800 rounded-md transition-colors"
            style={{ color: muted }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — accordion sections */}
        <div
          className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4 space-y-1"
          style={{ scrollbarGutter: 'stable' }}
        >
          {/* Owners */}
          <AccordionSection
            title="Owners"
            icon={<User size={16} />}
            expanded={expandedSections.owners}
            onToggle={() => toggleSection('owners')}
            isDark={isDark}
          >
            <div className="pt-1">
              <h4 className="font-medium mb-1 text-sm">Stock owners</h4>
              <p className="text-xs text-gray-400 mb-2">
                People or teams assigned stock when checking out or adding stock.
              </p>

              {error && (
                <p className="text-xs mb-2" style={{ color: '#fca5a5' }}>{error}</p>
              )}

              {isLoading && owners.length === 0 && (
                <p className="text-xs text-gray-400 py-3">Loading…</p>
              )}

              {!isLoading && owners.length === 0 && !adding && (
                <p className="text-xs text-gray-400 py-2">No owners yet. Add one below.</p>
              )}

              <div
                className="rounded-md overflow-hidden"
                style={{ backgroundColor: surface, border: `1px solid ${border}` }}
              >
                {owners.map(owner => (
                  <OwnerRow
                    key={owner.id}
                    owner={owner}
                    isDark={isDark}
                    onRename={(id, name, notes) => updateOwner(id, { name, notes })}
                    onDelete={id => removeOwner(id)}
                  />
                ))}

                {adding ? (
                  <div
                    className="flex items-start gap-2.5 px-2 py-2"
                    style={{ borderTop: owners.length > 0 ? `1px solid ${border}` : undefined }}
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                      style={{ backgroundColor: isDark ? '#374151' : '#475569' }}
                    >
                      <User size={12} style={{ color: muted }} />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <input
                        ref={newNameRef}
                        type="text"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') void submitAdd();
                          if (e.key === 'Escape') cancelAdd();
                        }}
                        className="text-sm px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                        placeholder="Owner name…"
                      />
                      <input
                        type="text"
                        value={newNotes}
                        onChange={e => setNewNotes(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') void submitAdd();
                          if (e.key === 'Escape') cancelAdd();
                        }}
                        className="text-xs px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{ backgroundColor: inputBg, borderColor: border, color: muted }}
                        placeholder="Notes (optional)…"
                      />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                      <button
                        type="button"
                        onClick={() => void submitAdd()}
                        disabled={!newName.trim() || saving}
                        className="p-1.5 rounded transition-colors disabled:opacity-40"
                        style={{ backgroundColor: isDark ? '#1a3a1a' : '#14532d', color: '#4ade80' }}
                        title="Add owner"
                      >
                        <Check size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={cancelAdd}
                        className="p-1.5 rounded transition-colors"
                        style={{ backgroundColor: isDark ? '#2b3140' : '#334155', color: muted }}
                        title="Cancel"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="px-2 py-1.5"
                    style={{ borderTop: owners.length > 0 ? `1px solid ${border}` : undefined }}
                  >
                    <button
                      type="button"
                      onClick={() => setAdding(true)}
                      className="inline-flex items-center gap-1.5 text-xs rounded px-2 py-1.5 transition-colors text-blue-400 hover:bg-slate-800/50"
                    >
                      <Plus size={13} />
                      Add owner
                    </button>
                  </div>
                )}
              </div>
            </div>
          </AccordionSection>

          {/* Suppliers */}
          <AccordionSection
            title="Suppliers"
            icon={<Truck size={16} />}
            expanded={expandedSections.suppliers}
            onToggle={() => toggleSection('suppliers')}
            isDark={isDark}
          >
            <div className="pt-1">
              <h4 className="font-medium mb-1 text-sm">Supplier list</h4>
              <p className="text-xs text-gray-400 mb-2">
                Options used when marking stock requests as ordered.
              </p>
              <div
                className="rounded-md overflow-hidden"
                style={{ backgroundColor: surface, border: `1px solid ${border}` }}
              >
                <ManageSuppliersPanel
                  isDark={isDark}
                  compact
                  theme={supplierTheme}
                />
              </div>
            </div>
          </AccordionSection>

          {/* Requests */}
          <AccordionSection
            title="Requests"
            icon={<ShieldCheck size={16} />}
            expanded={expandedSections.requests}
            onToggle={() => toggleSection('requests')}
            isDark={isDark}
          >
            <div className="pt-1 space-y-5">
              <div>
                <h4 className="font-medium mb-1 text-sm">List layout</h4>
                <p className="text-xs text-gray-400 mb-2">
                  By item shows every requested item in one sheet (like a Favro board).
                  By request groups items under each stock request.
                </p>
                <div
                  className="inline-flex rounded-md overflow-hidden"
                  style={{ border: `1px solid ${border}` }}
                >
                  <button
                    type="button"
                    onClick={() => setRequestsViewMode('by_item')}
                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: requestsViewMode === 'by_item'
                        ? (isDark ? '#1d2535' : '#1e3a5f')
                        : 'transparent',
                      color: requestsViewMode === 'by_item' ? '#93c5fd' : text,
                    }}
                  >
                    By item
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestsViewMode('by_request')}
                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: requestsViewMode === 'by_request'
                        ? (isDark ? '#1d2535' : '#1e3a5f')
                        : 'transparent',
                      color: requestsViewMode === 'by_request' ? '#93c5fd' : text,
                      borderLeft: `1px solid ${border}`,
                    }}
                  >
                    By request
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-1 text-sm">Default managers</h4>
                <p className="text-xs text-gray-400 mb-2">
                  Assigned when a request is submitted. They review lines, propose or accept
                  substitutes, give final approval, and place orders.
                </p>

                {settingsError && (
                  <p className="text-xs mb-2" style={{ color: '#fca5a5' }}>{settingsError}</p>
                )}

                {loadingSettings && !settings ? (
                  <p className="text-xs text-gray-400 py-3">Loading…</p>
                ) : users.length === 0 ? (
                  <p className="text-xs text-gray-400">No users found in this collection.</p>
                ) : (
                <div
                  className="rounded-md overflow-hidden"
                  style={{ backgroundColor: surface, border: `1px solid ${border}` }}
                >
                  <div className="flex flex-col gap-0.5 p-1.5">
                    {users.map(u => {
                      const isSelected = ordererIds.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          className="flex items-center gap-2.5 rounded px-2 py-1.5 cursor-pointer transition-colors"
                          style={{
                            backgroundColor: isSelected
                              ? (isDark ? '#1d2e3a' : '#1e3a5f')
                              : 'transparent',
                          }}
                          onMouseEnter={e => {
                            if (!isSelected) {
                              (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? '#262b35' : '#334155';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isSelected) {
                              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: isDark ? '#374151' : '#475569' }}
                          >
                            <ShieldCheck size={11} style={{ color: isSelected ? '#60a5fa' : muted }} />
                          </div>
                          <span
                            className="text-sm flex-1"
                            style={{ color: isSelected ? '#93c5fd' : text }}
                          >
                            {u.displayName}
                          </span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOrderer(u.id)}
                            className="rounded"
                          />
                        </label>
                      );
                    })}
                  </div>
                  <div
                    className="flex items-center justify-end gap-2 px-2.5 py-2"
                    style={{ borderTop: `1px solid ${border}` }}
                  >
                    {approversSaved && (
                      <span className="text-xs text-green-500 flex items-center gap-1">
                        <Check size={12} /> Saved
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => void handleSaveApprovers()}
                      disabled={savingApprovers}
                      className="inline-flex items-center gap-1.5 text-xs rounded px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50"
                    >
                      {savingApprovers ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </AccordionSection>
        </div>
      </div>
    </>
  );
};

export default StockPreferencesDrawer;
